import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import ResmoMascot from "../components/ResmoMascot";
import NewsFeed from "../components/NewsFeed";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import {
  Target, MessageCircle, Brain, Heart, TrendingUp, Sparkles,
  ChevronDown, ChevronUp, ListChecks, Mic, ArrowRight, Trash2, Briefcase,
} from "lucide-react";

const SECTIONS = [
  {
    id: "interview",
    icon: <MessageCircle size={22} strokeWidth={2.5} />,
    badge: "10 tips",
    title: "10 Interview Tips",
    subtitle: "Before, during, after the room.",
    items: [
      ["Company research", "Study their recent news, values, and challenges before you show up."],
      ["Question prep", "Write 3–5 smart questions that show you've done your homework."],
      ["Success stories", "Practice short, clear examples of your wins for the most common questions."],
      ["First impression", "Arrive 10 minutes early. Be nice to everyone — receptionist included."],
      ["Body language", "Sit up, eye contact, no nervous habits."],
      ["Answer structure", "Keep responses under 90 seconds. Lead with results."],
      ["Problem-solving mindset", "Show how you can solve their specific challenges, not yours."],
      ["Energy level", "Real enthusiasm, not desperation."],
      ["Specific examples", "Numbers and facts beat adjectives."],
      ["Thank-you follow-up", "Specific note within 24 hours referencing something you discussed."],
    ],
  },
  {
    id: "vp-advancement",
    icon: <TrendingUp size={22} strokeWidth={2.5} />,
    badge: "Senior career",
    title: "What Actually Gets You to VP",
    subtitle: "(High performers tend to bet on the wrong things.)",
    twoCol: true,
    twoColData: {
      hopeful: [
        "More books", "One breakthrough conversation", "The right insight",
        "Another certification", "Better performance metrics",
        "“If I just execute better…”", "“I just need to wait my turn”",
      ],
      reality: [
        "Identity shift", "Political fluency", "Executive narrative control",
        "Strategic visibility", "Sponsor advocacy", "Rewiring self-perception",
        "Sustained coach partnership", "Language pattern change",
        "Nervous-system regulation", "Tolerance for ambiguity",
        "Self-trust under pressure", "Public-imperfection tolerance",
        "Internal validation", "Influence beyond presence",
      ],
    },
  },
  {
    id: "self-talk",
    icon: <Brain size={22} strokeWidth={2.5} />,
    badge: "Mindset",
    title: "9 Ways to Reframe Your Self-Talk",
    subtitle: "So it serves instead of sabotages.",
    items: [
      ["Recognize the pattern", "Identify negative triggers."],
      ["Question the thought", "Is it facts, or an assumption?"],
      ["Act like a friend", "What would you tell someone else?"],
      ["Find middle-ground thoughts", "Move from “I can't” to “I am learning.”"],
      ["Focus on progress, not perfection", "Celebrate small wins."],
      ["Reframe challenges as learning", "What can I learn here?"],
      ["Detach from your thoughts", "They're just thoughts, not truth."],
      ["Practice self-compassion", "Treat yourself with kindness."],
      ["Identify strengths", "Recall your past achievements."],
    ],
  },
  {
    id: "likeable",
    icon: <Heart size={22} strokeWidth={2.5} />,
    badge: "Habits",
    title: "12 Habits of Exceptionally Likeable People",
    subtitle: "Small habits, big impact.",
    items: [
      ["Ask questions", "Show genuine curiosity."],
      ["Put away your phone", "Be fully present."],
      ["Be genuine", "Authenticity builds trust."],
      ["Don't judge", "Seek to understand first."],
      ["Don't seek attention", "Confidence is quiet."],
      ["Be consistent", "Reliability builds trust."],
      ["Use positive body language", "Smile, eye contact, open posture."],
      ["Make a strong first impression", "Warmth + confidence + posture."],
      ["Use people's names", "Personal, attentive, respectful."],
      ["Smile", "Genuine smiles are contagious."],
      ["Know when to open up", "Build trust gradually."],
      ["Use appropriate touch", "Handshake, pat — when welcomed."],
    ],
  },
  {
    id: "mind",
    icon: <Sparkles size={22} strokeWidth={2.5} />,
    badge: "Focus",
    title: "The Mind You Feed Wins",
    subtitle: "Your mind grows or withers based on what you feed it daily.",
    twoCol: true,
    twoColData: {
      hopeful: ["Overthinking", "Constant comparison", "Holding grudges", "Negative self-talk", "Consuming drama", "Staying comfortable", "Fear-based decisions"],
      reality: ["Gratitude", "Learning consistently", "Choosing courage", "Letting go", "Positive self-talk", "Seeking challenges", "Protecting your peace"],
      hopefulLabel: "Drains the mind",
      realityLabel: "Nurtures the mind",
    },
  },
];


function ItemList({ items }) {
  return (
    <ol className="space-y-3 list-none">
      {items.map(([h, t], i) => (
        <li key={i} className="flex gap-3">
          <div
            className="font-mono-rm text-xs uppercase tracking-widest font-bold flex-shrink-0 w-8 h-8 rounded-md flex items-center justify-center"
            style={{ background: "#0F1B2D", color: "white" }}
          >
            {String(i + 1).padStart(2, "0")}
          </div>
          <div>
            <div className="font-bold text-sm">{h}</div>
            <div className="text-sm" style={{ color: "#475569" }}>{t}</div>
          </div>
        </li>
      ))}
    </ol>
  );
}


function TwoCol({ data }) {
  const hopefulLabel = data.hopefulLabel || "What feels productive";
  const realityLabel = data.realityLabel || "What actually moves you";
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="p-4 rounded-xl" style={{ border: "3px solid #0F1B2D", background: "white" }}>
        <div className="rm-label mb-2" style={{ color: "#991B1B" }}>{hopefulLabel}</div>
        <ul className="space-y-1 text-sm">{data.hopeful.map((h, i) => <li key={i}>– {h}</li>)}</ul>
      </div>
      <div className="p-4 rounded-xl" style={{ border: "3px solid #0F1B2D", background: "#ECFDF5" }}>
        <div className="rm-label mb-2" style={{ color: "#065F46" }}>{realityLabel}</div>
        <ul className="space-y-1 text-sm">{data.reality.map((r, i) => <li key={i}>+ {r}</li>)}</ul>
      </div>
    </div>
  );
}


function AccordionCard({ id, icon, badge, title, subtitle, isOpen, onToggle, children, testId }) {
  return (
    <div className="rm-card-static overflow-hidden" data-testid={testId || `resource-${id}`}>
      <button
        className="w-full p-5 md:p-6 text-left flex items-start gap-4"
        onClick={onToggle}
        data-testid={`resource-toggle-${id}`}
        aria-expanded={isOpen}
      >
        <div
          className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: "#0F1B2D", color: "white", border: "3px solid #0F1B2D" }}
        >
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {badge && (
              <span className="rm-tag text-xs">{badge}</span>
            )}
          </div>
          <h2 className="font-display text-xl md:text-2xl uppercase leading-tight mt-1">{title}</h2>
          {subtitle && (
            <div className="font-mono-rm text-xs uppercase tracking-widest mt-1" style={{ color: "#475569" }}>
              {subtitle}
            </div>
          )}
        </div>
        <ChevronDown
          size={20}
          className="flex-shrink-0 mt-1 transition-transform duration-200"
          style={{ transform: isOpen ? "rotate(180deg)" : "none" }}
        />
      </button>
      {isOpen && (
        <div className="px-5 md:px-6 pb-6" data-testid={`resource-panel-${id}`}>
          {children}
        </div>
      )}
    </div>
  );
}


function InterviewBankHistory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/interview-questions");
        setItems(data);
      } catch {
        /* ignore */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleDelete = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm("Delete this bank?")) return;
    try {
      await api.delete(`/interview-questions/${id}`);
      setItems((arr) => arr.filter((x) => x.id !== id));
    } catch {
      /* ignore */
    }
  };

  if (loading) return <div className="font-mono-rm text-sm" style={{ color: "#475569" }}>Loading…</div>;
  if (items.length === 0) return null;

  return (
    <div className="mt-4">
      <div className="rm-label mb-2">Your recent banks</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((b) => (
          <Link
            key={b.id}
            to={`/interview-questions/${b.id}`}
            className="rm-card p-4 flex items-center justify-between gap-3"
            data-testid={`bank-card-${b.id}`}
          >
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm truncate">{b.role}</div>
              <div className="font-mono-rm text-xs mt-1" style={{ color: "#475569" }}>
                {b.level} {b.focus?.length ? `· ${b.focus.slice(0, 2).join(" · ")}` : ""}
              </div>
            </div>
            <button
              onClick={(e) => handleDelete(b.id, e)}
              className="rm-btn rm-btn-ghost text-xs p-2"
              aria-label="Delete bank"
            >
              <Trash2 size={14} />
            </button>
          </Link>
        ))}
      </div>
    </div>
  );
}


export default function Resources() {
  const [openId, setOpenId] = useState(null);
  const [pulseOpen, setPulseOpen] = useState(false);

  const toggle = (id) => setOpenId((curr) => (curr === id ? null : id));

  return (
    <div className="min-h-screen rm-paper">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10 pb-20">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
          <div>
            <div className="rm-tag mb-3">Research &amp; Dev</div>
            <h1 className="font-display text-4xl md:text-5xl uppercase">
              Sharpen your <span className="rm-text-orange">edge.</span>
            </h1>
            <p className="text-base md:text-lg mt-2 max-w-2xl" style={{ color: "#475569" }}>
              Field-tested wisdom for interviews, mindset, and career advancement —
              the stuff Resmo recommends between bites.
            </p>
          </div>
          <ResmoMascot state="satisfied" size={140} />
        </div>

        {/* Job Market Pulse — collapsible (default closed) */}
        <div className="mb-6" data-testid="news-section">
          <button
            type="button"
            onClick={() => setPulseOpen((v) => !v)}
            className="w-full flex items-center gap-3 px-3 py-2.5 transition-colors"
            style={{
              background: pulseOpen ? "#0F1B2D" : "rgba(15,27,45,0.92)",
              border: "2px solid #0F1B2D",
              borderRadius: 8,
              boxShadow: "3px 3px 0 0 #0F1B2D",
              cursor: "pointer",
            }}
            data-testid="news-section-toggle"
            aria-expanded={pulseOpen}
          >
            <div className="flex-1 text-left min-w-0">
              <div className="font-mono-rm uppercase text-[9px] tracking-widest truncate"
                   style={{ color: "#FFC145", fontWeight: 700 }}>
                Live news ticker · curated picks
              </div>
              <div className="font-display uppercase text-base leading-none truncate"
                   style={{ color: "white", letterSpacing: "-0.01em" }}>
                Job Market Pulse
              </div>
            </div>
            <div style={{ color: "#FFC145" }}>
              {pulseOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </div>
          </button>
          {pulseOpen && (
            <div className="mt-3" data-testid="news-section-body">
              <NewsFeed />
            </div>
          )}
        </div>

        {/* Featured: Interview Questions Generator */}
        <div
          className="rm-card-static p-6 md:p-8 mb-6"
          style={{ background: "#0F1B2D", color: "white" }}
          data-testid="interview-generator-card"
        >
          <div className="flex flex-wrap items-center gap-6 justify-between">
            <div className="flex items-start gap-4 flex-1 min-w-[260px]">
              <div
                className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: "#FF6A00", color: "white", border: "3px solid #FF6A00" }}
              >
                <ListChecks size={22} strokeWidth={2.5} />
              </div>
              <div>
                <div className="rm-tag mb-2" style={{ background: "#FFC145", borderColor: "#FFC145", color: "#0F1B2D" }}>
                  <Sparkles size={12} /> New
                </div>
                <h2 className="font-display text-2xl md:text-3xl uppercase leading-tight">
                  Generate an <span className="rm-text-orange">interview bank.</span>
                </h2>
                <p className="text-sm mt-2 max-w-xl" style={{ color: "#CBD5E1" }}>
                  Pick a role + level → get 20–26 questions across 4–5 categories,
                  trap questions, and smart questions to ask back. Optionally
                  personalize with your last tailored analysis.
                </p>
              </div>
            </div>
            <Link
              to="/resources/interview/new"
              className="rm-btn rm-btn-secondary"
              data-testid="interview-generator-cta"
            >
              <ArrowRight size={16} /> Build my bank
            </Link>
          </div>
          <InterviewBankHistory />
        </div>

        {/* Stepping Stones — interview retrospective journal */}
        <div
          className="rm-card-static p-6 md:p-8 mb-6"
          style={{ background: "#FFFBEB" }}
          data-testid="stepping-stones-card"
        >
          <div className="flex flex-wrap items-center gap-6 justify-between">
            <div className="flex items-start gap-4 flex-1 min-w-[260px]">
              <div
                className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: "#FFC145", color: "#0F1B2D", border: "3px solid #0F1B2D" }}
              >
                <Heart size={22} strokeWidth={2.5} />
              </div>
              <div>
                <div className="rm-tag mb-2"
                     style={{ background: "#FF6A00", borderColor: "#FF6A00", color: "white" }}>
                  <Sparkles size={12} /> New
                </div>
                <h2 className="font-display text-2xl md:text-3xl uppercase leading-tight">
                  Stepping <span className="rm-text-orange">stones.</span>
                </h2>
                <p className="text-sm mt-2 max-w-xl" style={{ color: "#475569" }}>
                  Every interview is a stepping stone — not a stop sign. Log the wins,
                  what you'll focus on next, any feedback the interviewers shared, and
                  contacts to keep warm. Your growth log on the way to the dream role.
                </p>
              </div>
            </div>
            <Link
              to="/stepping-stones"
              className="rm-btn rm-btn-primary"
              data-testid="stepping-stones-cta"
            >
              <ArrowRight size={16} /> Open my log
            </Link>
          </div>
        </div>

        {/* Research & Dev accordion */}
        <div className="rm-label mb-3">Research &amp; Dev</div>
        <div className="space-y-3" data-testid="resources-accordion">
          {SECTIONS.map((s) => (
            <AccordionCard
              key={s.id}
              id={s.id}
              icon={s.icon}
              badge={s.badge}
              title={s.title}
              subtitle={s.subtitle}
              isOpen={openId === s.id}
              onToggle={() => toggle(s.id)}
            >
              {s.twoCol ? <TwoCol data={s.twoColData} /> : <ItemList items={s.items} />}
            </AccordionCard>
          ))}
        </div>

        <div className="rm-card-static p-6 md:p-8 mt-10" style={{ background: "#0F1B2D", color: "white" }}>
          <div className="flex flex-wrap items-center gap-6 justify-between">
            <div>
              <div className="rm-tag mb-2" style={{ background: "#FFC145", borderColor: "#FFC145" }}>
                <Target size={14} /> Try this next
              </div>
              <h2 className="font-display text-2xl md:text-3xl uppercase leading-tight">
                Pair the wisdom with a tailored resume.
              </h2>
            </div>
            <Link to="/feed-job" className="rm-btn rm-btn-secondary">
              Tailor a resume now →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
